import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import { Toaster } from 'sonner';
import App from './App.js';
import { queryClient } from './lib/queryClient.js';
import { useAuthStore } from './stores/authStore.js';
import { loadAndApplyTokens } from './services/uiSystem.service.js';
import { useActiveBrandStore } from './stores/activeBrand.store.js';
import { useDesktopStore } from './stores/desktopStore.js';
import './index.css';

/**
 * Boot the chrome theme: apply the PR-3 default instance, then layer the active
 * brand on top (cascade "A"). `activeBrand.init()` itself re-applies the defaults
 * before layering the brand, so the two are ordered + idempotent. The owned-brand
 * list (header selector) is fetched in parallel. On logout or a principal switch, brand, desktop
 * and query-cache state reset.
 */
function bootBrandTheme() {
  void loadAndApplyTokens();
  void useActiveBrandStore.getState().init();
  void useActiveBrandStore.getState().loadOwnedBrands();
}

// Load UI tokens + active brand on boot if authenticated.
if (useAuthStore.getState().isAuthenticated) {
  bootBrandTheme();
}
// Re-apply whenever auth state transitions to authenticated; reset on logout.
useAuthStore.subscribe((state, prev) => {
  const signedOut = prev.isAuthenticated && !state.isAuthenticated;
  // Re-login over a live session (back to /login, no reload) swaps the principal without a logout.
  const switched =
    prev.isAuthenticated && state.isAuthenticated && state.user?.id !== prev.user?.id;
  if (signedOut || switched) {
    useActiveBrandStore.getState().reset();
    // A soft logout keeps the tab's in-memory stores (gh#2561); most query keys carry no user/tenant.
    useDesktopStore.getState().reset();
    queryClient.clear();
  }
  if (state.isAuthenticated && (!prev.isAuthenticated || switched)) {
    bootBrandTheme();
  }
});

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <App />
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </QueryClientProvider>
  </React.StrictMode>,
);
